<template>
  <v-app id="inspire">
    <v-main class="client-cabinet-page">
      <!-- FT-200 STEP-04: вход — ClientLogin (бренд по коду, ошибки в форме, пара во вкладке). -->
      <client-login
        v-if="booking == null"
        :key="loginKey"
        :company-code="companyCode"
        :initial-booking-id="loginPair ? loginPair.bookingId : ''"
        :initial-last-name="loginPair ? loginPair.lastName : ''"
        :auto-submit="!!loginPair"
        @logged-in="onLoggedIn"
      ></client-login>

      <v-container v-else fluid class="client-cabinet" data-testid="client-cabinet">
        <cabinet-header ref="header" :booking="booking" :company="company" :client="client"></cabinet-header>

        <p v-if="refreshFailed" class="client-cabinet-error" role="status" data-testid="client-page-refresh-failed">
          {{ $t("client_page.cabinet.refresh_failed") }}
        </p>

        <div class="client-cabinet-dashboard" data-testid="client-cabinet-dashboard">
          <aside class="client-cabinet-current" :aria-label="$t('client_page.cabinet.payment.title')" data-testid="client-cabinet-current">
            <div class="client-cabinet-sticky">
              <payment-block
                :booking="booking"
                :company="company"
                :client="client"
                :to-pay="toPay"
                :next-charge="nextCharge"
                :booking-id="booking_id"
                :last-name="last_name"
              ></payment-block>
            </div>
          </aside>

          <div class="client-cabinet-content" data-testid="client-cabinet-content">
            <action-required :documents="booking.generated_documents || []"></action-required>

            <card-block
              :client="client"
              :company="company"
              :tinkoff-payments="tinkoff_payments"
              :booking-id="booking_id"
              :last-name="last_name"
              :active-deposit-hold="activeDepositHold"
              @changed="onCardChanged"
            ></card-block>

            <documents-block :documents="booking.generated_documents || []"></documents-block>

            <details-block :booking="booking" :company="company"></details-block>
            <amount-breakdown :booking="booking" :company="company" :client="client" :to-pay="toPay"></amount-breakdown>
            <payment-history
              :booking="booking"
              :company="company"
              :stripe-payments="stripe_payments"
              :yandex-pay-payments="yandex_pay_payments"
              :tinkoff-payments="tinkoff_payments"
            ></payment-history>
            <prolongs-block v-if="prolongs.length" :prolongs="prolongs" :company="company"></prolongs-block>

            <div class="client-cabinet-actions">
              <v-btn
                v-if="calendarIcs"
                outlined
                color="primary"
                min-height="44"
                data-testid="client-page-calendar"
                data-field="calendar_ics"
                @click="saveCalendar"
              >
                <v-icon left aria-hidden="true">mdi-calendar-export</v-icon>
                {{ $t("client_page.cabinet.calendar") }}
              </v-btn>
              <v-btn text color="primary" min-height="44" data-testid="client-page-other-booking" @click="openOtherBooking">
                {{ $t("client_page.cabinet.other_booking") }}
              </v-btn>
            </div>
          </div>
        </div>

        <footer class="client-cabinet-footer">{{ $t("client_page.cabinet.powered_by") }}</footer>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
// FT-200 STEP-05 (REQ-08, REQ-09, REQ-11, REQ-12): кабинет клиента — оркестрация блоков.
//
// Вход — ClientLogin (Task 4): пара номер + фамилия живёт во вкладке (clientCabinetSession), не в адресе (REQ-07).
// Кабинет читает только новые ключи ответа (схема `new`, CTR-01): старые ключи PR A он не трогает, поэтому одинаково
// работает на ответе PR A и PR B. Любой новый ключ может прийти null (сбой его вычисления на сервере) — блок,
// которому он нужен, тогда не показывается.
//
// Подписок ActionCable нет (REQ-12, NEG-05): каналы платежей открыты по client_id без авторизации подписки (NS-08).
// Свежие данные — перечитыванием: при возвращении во вкладку не чаще раза в 30 с, а после начатой из кабинета оплаты
// или привязки — сразу (клиент вернулся из банка).
import "@fontsource/ibm-plex-sans/400.css";
import "@fontsource/ibm-plex-sans/500.css";
import "@fontsource/ibm-plex-sans/600.css";
import "@fontsource/ibm-plex-sans/700.css";
import clientCabinetSession from "@/utils/clientCabinetSession";
import { saveCalendar } from "@/utils/clientCabinetCalendar";
import { reportServerError } from "@/utils/clientCabinetView";
import ClientLogin, { LOGIN_PATH } from "./client_page/ClientLogin";
import CabinetHeader from "./client_page/CabinetHeader";
import ActionRequired from "./client_page/ActionRequired";
import PaymentBlock from "./client_page/PaymentBlock";
import CardBlock from "./client_page/CardBlock";
import DocumentsBlock from "./client_page/DocumentsBlock";
import DetailsBlock from "./client_page/DetailsBlock";
import AmountBreakdown from "./client_page/AmountBreakdown";
import PaymentHistory from "./client_page/PaymentHistory";
import ProlongsBlock from "./client_page/ProlongsBlock";

export const REFRESH_INTERVAL_MS = 30 * 1000;

export default {
  name: "ClientPage",
  components: {
    ClientLogin,
    CabinetHeader,
    ActionRequired,
    PaymentBlock,
    CardBlock,
    DocumentsBlock,
    DetailsBlock,
    AmountBreakdown,
    PaymentHistory,
    ProlongsBlock,
  },
  data() {
    return {
      booking: null,
      company: null,
      client: null,
      stripe_payments: [],
      yandex_pay_payments: [],
      tinkoff_payments: [],
      toPay: null,
      nextCharge: null,
      activeDepositHold: null,
      calendarIcs: null,
      last_name: null,
      booking_id: null,
      // FT-200 STEP-04: код компании из ссылки проката (бренд на входе) и пара для автовхода.
      companyCode: null,
      loginPair: null,
      loginKey: 0,
      refreshFailed: false,
      lastReadAt: 0,
      reading: false,
      // Поколение входа: ответ перечитывания, начатого до выхода, нового входа или ухода со страницы, отбрасывается —
      // иначе запоздалый ответ бронирования A ляжет поверх входа в B (или его 404 разлогинит B).
      loginGeneration: 0,
    };
  },
  computed: {
    prolongs() {
      return (this.booking && this.booking.prolongs) || [];
    },
  },
  created() {
    // FT-200 REQ-07: ссылка проката входит и убирает пару из адреса; без ссылки — пара этой вкладки.
    const fromLink = clientCabinetSession.consumeQuery(this.$route, this.$router);
    this.companyCode = (fromLink && fromLink.companyCode) || null;
    // Пришла ссылка — входим только по её паре: пара вкладки может быть другого проката, чем бренд из `c`.
    this.loginPair = clientCabinetSession.entryPair(fromLink);
    this.initialTitle = typeof document !== "undefined" ? document.title : "";
  },
  mounted() {
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  },
  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.loginGeneration += 1;
    if (this.initialTitle !== undefined) document.title = this.initialTitle;
  },
  // Жест «потяни, чтобы обновить» в приложении — перечитать бронирование без ремаунта.
  pullToRefresh() {
    return this.booking ? this.findBooking() : Promise.resolve();
  },
  methods: {
    // FT-200 STEP-04: вход выполнил ClientLogin.
    onLoggedIn({ bookingId, lastName, data }) {
      this.loginGeneration += 1;
      this.reading = false;
      this.booking_id = bookingId;
      this.last_name = lastName;
      this.applyBookingResponse(data);
      // REQ-13: форма входа заменена кабинетом — фокус на h1, иначе он остаётся на удалённой кнопке (body).
      this.$nextTick(() => {
        if (this.$refs.header) this.$refs.header.focusTitle();
      });
    },
    applyBookingResponse(data) {
      this.booking = data.booking;
      this.company = data.company || null;
      this.client = data.client || null;
      this.stripe_payments = data.stripe_payments || [];
      this.yandex_pay_payments = data.yandex_pay_payments || [];
      this.tinkoff_payments = data.tinkoff_payments || [];
      this.toPay = data.to_pay === undefined ? null : data.to_pay;
      this.nextCharge = data.next_charge || null;
      this.activeDepositHold = data.active_deposit_hold || null;
      this.calendarIcs = data.calendar_ics || null;
      this.lastReadAt = Date.now();
      this.refreshFailed = false;
      this.updateTitle();
    },
    // U4: «Бронирование №N — {прокат}».
    updateTitle() {
      const company = this.company && this.company.name;
      document.title = company
        ? this.$t("client_page.cabinet.document_title", { id: this.booking.id, company })
        : this.$t("client_page.cabinet.title", { id: this.booking.id });
    },
    // Перечитать бронирование по паре вкладки. Отказ «не найдено» (бронирование удалено, фамилию поправили) — пара
    // стирается, клиент видит форму входа. Прочие отказы — данные остаются, строка «не удалось обновить».
    // Ошибку не печатаем в консоль: ошибка axios несёт пару в config.data.
    findBooking() {
      if (!this.booking_id || !this.last_name || this.reading) return Promise.resolve();
      this.reading = true;
      const generation = this.loginGeneration;
      const stale = () => generation !== this.loginGeneration;
      return this.axios
        .post(LOGIN_PATH, { booking_id: this.booking_id, last_name: this.last_name })
        .then((response) => {
          if (stale()) return;
          const data = (response && response.data) || {};
          if (data.error || !data.booking) {
            this.signOut();
            return;
          }
          this.applyBookingResponse(data);
        })
        .catch((error) => {
          if (stale()) return;
          const status = error && error.response && error.response.status;
          if (status === 404 || status === 422) {
            this.signOut();
            return;
          }
          this.refreshFailed = true;
          reportServerError(this, "ClientCabinet: перечитывание бронирования", error);
        })
        .then(() => {
          if (!stale()) this.reading = false;
        });
    },
    onVisibilityChange() {
      if (document.visibilityState !== "visible" || !this.booking) return;
      if (this.shouldRefresh(Date.now())) this.findBooking();
    },
    // Не чаще раза в 30 с; после начатой в этой вкладке оплаты или привязки — сразу (возврат из банка).
    shouldRefresh(now) {
      const op = clientCabinetSession.operation();
      if (op && op.startedAt > this.lastReadAt) return true;
      return now - this.lastReadAt >= REFRESH_INTERVAL_MS;
    },
    signOut() {
      this.loginGeneration += 1;
      this.reading = false;
      clientCabinetSession.clear();
      this.booking = null;
      this.company = null;
      this.client = null;
      this.loginPair = null;
      this.loginKey += 1;
      if (this.initialTitle !== undefined) document.title = this.initialTitle;
    },
    // Блок карты сообщил, что карты больше нет (отвязана): скрыть её сразу, не дожидаясь ответа, и перечитать.
    onCardChanged(change) {
      if (change && change.cardBound === false && this.client) {
        this.client = { ...this.client, tinkoff_card_bound: false };
      }
      return this.findBooking();
    },
    // «Открыть другое бронирование»: пара вкладки стирается, форма входа пустая и без автовхода.
    openOtherBooking() {
      this.companyCode = null;
      this.signOut();
    },
    // Календарь — файл из calendar_ics ответа (DEC-01): веб — скачивание, натив — файл и «Поделиться».
    saveCalendar() {
      return saveCalendar(this.calendarIcs, `booking-${this.booking.id}.ics`, this.$t("client_page.cabinet.calendar"));
    },
  },
};
</script>

<style>
/* REQ-13: видимое кольцо фокуса с клавиатуры — штатная подложка Vuetify (9–16 %) почти не видна. #1976d2 на белом 4,6:1,
   #64b5f6 на тёмном фоне 8:1 (≥ 3:1). Диалоги рисуются вне .client-cabinet — класс client-cabinet-dialog. */
.client-cabinet .v-btn:focus-visible,
.client-cabinet .v-expansion-panel-header:focus-visible,
.client-cabinet a:focus-visible,
.client-cabinet-dialog .v-btn:focus-visible,
.client-cabinet-dialog a:focus-visible,
.client-cabinet-dialog .v-input--selection-controls__input input:focus-visible {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

.theme--dark .client-cabinet .v-btn:focus-visible,
.theme--dark .client-cabinet .v-expansion-panel-header:focus-visible,
.theme--dark .client-cabinet a:focus-visible,
.theme--dark .client-cabinet-dialog .v-btn:focus-visible,
.theme--dark .client-cabinet-dialog a:focus-visible {
  outline-color: #64b5f6;
}

.client-cabinet-dialog .primary.v-btn:not(.v-btn--outlined) {
  background-color: #1976d2 !important;
  color: #ffffff !important;
}

.client-cabinet-dialog .v-btn.v-btn--outlined,
.client-cabinet-dialog .v-btn.v-btn--text {
  border-color: #1976d2;
  color: #1976d2 !important;
}

.theme--dark .client-cabinet-dialog .primary.v-btn:not(.v-btn--outlined) {
  background-color: #2196f3 !important;
  color: rgba(0, 0, 0, 0.87) !important;
}

.theme--dark .client-cabinet-dialog .v-btn.v-btn--outlined,
.theme--dark .client-cabinet-dialog .v-btn.v-btn--text {
  border-color: #64b5f6;
  color: #64b5f6 !important;
}

.client-cabinet-page {
  --cabinet-bg: #f5f5f5;
  --cabinet-fg: rgba(0, 0, 0, 0.87);
  --cabinet-muted: rgba(0, 0, 0, 0.6);
  --cabinet-subtle: rgba(0, 0, 0, 0.6);
  --cabinet-surface: #ffffff;
  --cabinet-surface-2: #fafafa;
  --cabinet-surface-3: #eeeeee;
  --cabinet-line: #e0e0e0;
  --cabinet-line-strong: #bdbdbd;
  --cabinet-accent: #1976d2;
  --cabinet-accent-hover: #1565c0;
  --cabinet-accent-ink: #ffffff;
  --cabinet-accent-soft: #e3f2fd;
  --cabinet-debt: #c62828;
  --cabinet-debt-soft: #ffebee;
  --cabinet-success: #2e7d32;
  --cabinet-success-soft: #e8f5e9;
  --cabinet-warning: #a15c00;
  --cabinet-warning-soft: #fff3e0;
  --cabinet-focus: #1976d2;
  --cabinet-overlay: rgba(0, 0, 0, 0.04);
  --cabinet-shadow: rgba(0, 0, 0, 0.12);
  --cabinet-shadow-strong: rgba(0, 0, 0, 0.2);
  min-width: 320px;
  background: var(--cabinet-bg);
  color: var(--cabinet-fg);
}

.theme--dark .client-cabinet-page {
  --cabinet-bg: #121212;
  --cabinet-fg: rgba(255, 255, 255, 0.87);
  --cabinet-muted: rgba(255, 255, 255, 0.6);
  --cabinet-subtle: rgba(255, 255, 255, 0.6);
  --cabinet-surface: #1e1e1e;
  --cabinet-surface-2: #242424;
  --cabinet-surface-3: #2c2c2c;
  --cabinet-line: rgba(255, 255, 255, 0.12);
  --cabinet-line-strong: rgba(255, 255, 255, 0.24);
  --cabinet-accent: #2196f3;
  --cabinet-accent-hover: #64b5f6;
  --cabinet-accent-ink: rgba(0, 0, 0, 0.87);
  --cabinet-accent-soft: rgba(33, 150, 243, 0.2);
  --cabinet-debt: #ef9a9a;
  --cabinet-debt-soft: rgba(239, 154, 154, 0.16);
  --cabinet-success: #81c784;
  --cabinet-success-soft: rgba(129, 199, 132, 0.16);
  --cabinet-warning: #ffb74d;
  --cabinet-warning-soft: rgba(255, 183, 77, 0.16);
  --cabinet-focus: #64b5f6;
  --cabinet-overlay: rgba(255, 255, 255, 0.08);
  --cabinet-shadow: rgba(0, 0, 0, 0.22);
  --cabinet-shadow-strong: rgba(0, 0, 0, 0.36);
}

.client-cabinet {
  width: min(1180px, 100%);
  max-width: 1180px;
  padding: 16px 16px 32px;
  color: var(--cabinet-fg);
  font-family: "IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-size: 16px;
  line-height: 1.5;
}

.client-cabinet-rental-header {
  position: relative;
  overflow: hidden;
  padding: 20px;
  border-radius: 24px;
  border: 1px solid var(--cabinet-line);
  background: var(--cabinet-surface);
  color: var(--cabinet-fg);
  box-shadow: 0 10px 28px var(--cabinet-shadow);
}

.client-cabinet-rental-header::after {
  position: absolute;
  right: -52px;
  bottom: -76px;
  width: 184px;
  height: 184px;
  border: 24px solid var(--cabinet-accent-soft);
  border-radius: 50%;
  content: "";
  pointer-events: none;
}

.client-cabinet-header__brand-row,
.client-cabinet-header__booking {
  position: relative;
  z-index: 1;
}

.client-cabinet-header__brand-row {
  display: flex;
  align-items: center;
  gap: 14px;
}

.client-cabinet-header__monogram {
  display: grid;
  flex: 0 0 auto;
  width: 52px;
  height: 52px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: var(--cabinet-accent);
  color: var(--cabinet-accent-ink);
  font-family: "IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-size: 19px;
  font-weight: 700;
}

.client-cabinet-header__brand-copy {
  min-width: 0;
}

.client-cabinet-header__company {
  margin: 0;
  color: var(--cabinet-fg);
  font-family: "IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  font-size: clamp(18px, 5.2vw, 28px);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.03em;
  overflow-wrap: anywhere;
}

.client-cabinet-header__phone,
.client-cabinet-header__email {
  display: flex;
  width: fit-content;
  align-items: center;
  min-height: 44px;
  margin-top: 1px;
  color: var(--cabinet-accent);
  font-size: 14px;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.client-cabinet-header__booking {
  display: grid;
  gap: 10px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--cabinet-line);
}

.client-cabinet-header__booking-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.client-cabinet-header__number,
.client-cabinet-header__customer {
  font-size: 14px;
  font-weight: 700;
}

.client-cabinet-header__state {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 28px;
  padding: 4px 10px;
  border: 1px solid var(--cabinet-line);
  border-radius: 999px;
  background: var(--cabinet-surface-2);
  font-size: 13px;
  font-weight: 700;
}

.client-cabinet-header__state::before {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
  content: "";
}

.client-cabinet-header__car {
  margin-top: 3px;
  font-size: 19px;
  font-weight: 700;
  line-height: 1.25;
}

.client-cabinet-header__dates {
  margin-top: 2px;
  font-size: 14px;
  font-weight: 500;
}

.client-cabinet-header__customer {
  margin: 0;
}

.client-cabinet-dashboard {
  display: grid;
  grid-template-areas: "current" "content";
  gap: 18px;
  margin-top: 18px;
}

.client-cabinet-current {
  grid-area: current;
  min-width: 0;
}

.client-cabinet-content {
  grid-area: content;
  min-width: 0;
}

.client-cabinet .primary.v-btn:not(.v-btn--outlined) {
  background-color: var(--cabinet-accent) !important;
  color: var(--cabinet-accent-ink) !important;
}

.client-cabinet .v-btn.v-btn--outlined,
.client-cabinet .v-btn.v-btn--text {
  border-color: var(--cabinet-accent);
  color: var(--cabinet-accent) !important;
}

.client-cabinet-current-card {
  --cabinet-state-accent: var(--cabinet-accent);
  overflow: hidden;
  margin-bottom: 0;
  border-radius: 24px;
  background: var(--cabinet-surface);
  box-shadow: inset 0 5px 0 var(--cabinet-state-accent), 0 12px 32px var(--cabinet-shadow);
}

.client-cabinet-current-card--debt {
  --cabinet-state-accent: var(--cabinet-debt);
}

.client-cabinet-current-card--paid {
  --cabinet-state-accent: var(--cabinet-success);
}

.client-cabinet-current-main {
  padding: 20px;
}

.client-cabinet-current-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.client-cabinet-current-title h2,
.client-cabinet-section-heading h2 {
  margin: 0;
  font-size: 20px;
  line-height: 1.25;
  letter-spacing: -0.02em;
}

.client-cabinet-state-badge {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 5px 9px;
  border-radius: 999px;
  background: var(--cabinet-accent-soft);
  color: var(--cabinet-accent);
  font-size: 12px;
  font-weight: 700;
}

.client-cabinet-current-card--debt .client-cabinet-state-badge {
  background: var(--cabinet-debt-soft);
  color: var(--cabinet-debt);
}

.client-cabinet-current-card--paid .client-cabinet-state-badge {
  background: var(--cabinet-success-soft);
  color: var(--cabinet-success);
}

.client-cabinet-amount-label {
  margin: 0;
  color: var(--cabinet-muted);
  font-size: 16px;
  font-weight: 700;
}

.client-cabinet-current-card--debt .client-cabinet-amount-label,
.client-cabinet-current-card--debt .client-cabinet-amount {
  color: var(--cabinet-debt);
}

.client-cabinet-amount {
  margin-top: 2px;
  font-size: clamp(36px, 10vw, 52px);
  font-weight: 700;
  line-height: 1.08;
  letter-spacing: -0.04em;
  font-variant-numeric: tabular-nums;
}

.client-cabinet-paid-title {
  margin: 0;
  color: var(--cabinet-success);
  font-size: clamp(30px, 9vw, 44px);
  font-weight: 700;
  line-height: 1.12;
  letter-spacing: -0.03em;
}

.client-cabinet-next-charge,
.client-cabinet-balance-row {
  margin-top: 15px;
  padding-top: 13px;
  border-top: 1px solid var(--cabinet-line);
  color: var(--cabinet-muted);
  font-size: 14px;
}

.client-cabinet-balance-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.client-cabinet-next-charge strong,
.client-cabinet-balance-row strong {
  color: var(--cabinet-fg);
  font-variant-numeric: tabular-nums;
}

.client-cabinet-current-main > .v-btn {
  margin-top: 12px;
  border-radius: 12px;
  font-weight: 700;
  text-transform: none;
}

.client-cabinet-offline-box {
  margin-top: 18px;
  padding: 14px;
  border-radius: 12px;
  background: var(--cabinet-surface-2);
}

.client-cabinet-offline-box strong {
  display: block;
}

.client-cabinet-offline-box p {
  margin: 4px 0 0;
  color: var(--cabinet-muted);
  font-size: 13px;
}

.client-cabinet-offline-box a {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  color: var(--cabinet-accent);
  font-weight: 700;
}

.client-cabinet-current-foot {
  padding: 11px 18px;
  border-top: 1px solid var(--cabinet-line);
  background: var(--cabinet-surface-2);
  color: var(--cabinet-muted);
  font-size: 12px;
  text-align: center;
}

.client-cabinet-section-card {
  margin-bottom: 16px;
  padding: 18px;
  border: 1px solid var(--cabinet-line);
  border-radius: 16px;
  background: var(--cabinet-surface);
}

.client-cabinet-section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}

.client-cabinet-section-heading p,
.client-cabinet-card-benefit p {
  margin: 5px 0 0;
  color: var(--cabinet-muted);
  font-size: 14px;
}

.client-cabinet-card-benefit {
  margin: 14px 0 0;
  color: var(--cabinet-muted);
  font-size: 14px;
}

.client-cabinet-section-action {
  margin-top: 14px;
}

.client-cabinet-linked-card,
.client-cabinet-checking-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid var(--cabinet-line);
}

.client-cabinet-checking-row {
  align-items: flex-start;
}

.client-cabinet-checking-row strong,
.client-cabinet-checking-row span {
  display: block;
}

.client-cabinet-checking-row span {
  margin-top: 2px;
  color: var(--cabinet-muted);
  font-size: 13px;
}

.client-cabinet-pending-dot {
  flex: 0 0 auto;
  width: 12px;
  height: 12px;
  margin-top: 5px !important;
  border: 3px solid var(--cabinet-warning-soft);
  border-radius: 50%;
  background: var(--cabinet-warning);
  box-shadow: 0 0 0 1px var(--cabinet-warning);
}

.client-cabinet-action-panel {
  display: grid;
  gap: 14px;
  margin-bottom: 16px;
  padding: 18px;
  border: 1px solid var(--cabinet-warning);
  border-radius: 16px;
  background: var(--cabinet-warning-soft);
}

.client-cabinet-action-panel h2,
.client-cabinet-plain-section h2 {
  margin: 0;
  color: var(--cabinet-fg);
  font-size: 19px;
  line-height: 1.3;
  letter-spacing: -0.02em;
}

.client-cabinet-action-panel p {
  margin: 5px 0 0;
  color: var(--cabinet-fg);
  font-size: 14px;
}

.client-cabinet-action-panel__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.client-cabinet-primary-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 160px;
  min-height: 48px;
  padding: 11px 18px;
  border-radius: 12px;
  background: var(--cabinet-accent);
  color: var(--cabinet-accent-ink) !important;
  font-weight: 700;
  text-decoration: none;
}

.client-cabinet-plain-section {
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid var(--cabinet-line-strong);
}

.client-cabinet-summary-grid {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px 18px;
  margin-top: 14px;
  padding: 14px;
  border-radius: 12px;
  background: var(--cabinet-surface);
  font-size: 14px;
}

.client-cabinet-summary-grid span {
  color: var(--cabinet-muted);
}

.client-cabinet-summary-grid strong {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.client-cabinet-summary-grid > *,
.client-cabinet-row > * {
  min-width: 0;
  overflow-wrap: anywhere;
}

.client-cabinet-due {
  color: var(--cabinet-debt);
}

.client-cabinet-disclosure {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  margin-top: 8px;
  padding: 7px 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--cabinet-accent);
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.client-cabinet-disclosure:focus-visible,
.client-cabinet-primary-link:focus-visible {
  outline: 3px solid var(--cabinet-focus);
  outline-offset: 3px;
}

.client-cabinet-section-heading p {
  margin: 5px 0 0;
  color: var(--cabinet-muted);
  font-size: 14px;
}

.client-cabinet-empty-state {
  margin: 14px 0 0;
  padding: 20px 16px;
  border: 1px dashed var(--cabinet-line-strong);
  border-radius: 12px;
  background: var(--cabinet-surface);
  color: var(--cabinet-muted);
  text-align: center;
}

.client-cabinet-block {
  margin-bottom: 24px;
}

.client-cabinet-block--action {
  border-left: 4px solid var(--cabinet-warning);
  padding-left: 12px;
}

.client-cabinet-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  column-gap: 12px;
  min-height: 44px;
}

.client-cabinet-list {
  list-style: none;
  padding-left: 0 !important;
}

.client-cabinet-list > li + li {
  border-top: 1px solid var(--cabinet-line);
}

.theme--dark .client-cabinet-list > li + li {
  border-top-color: var(--cabinet-line);
}

/* REQ-13: контраст текста ≥ 4,5:1 — #c62828 (5,6:1), #2e7d32 (5,1:1), вторичный rgba(0, 0, 0, 0.6) (5,7:1) на белом.
   На v-btn цвет задаёт .theme--light.v-btn (0,2,0) — селектор кабинета сильнее (0,3,0) и не зависит от порядка CSS. */
.client-cabinet-error,
.theme--light.v-btn.client-cabinet-error {
  color: #c62828;
}

.client-cabinet-ok {
  color: #2e7d32;
}

.client-cabinet-muted {
  color: var(--cabinet-muted);
}

.theme--dark .client-cabinet-error,
.theme--dark.v-btn.client-cabinet-error {
  color: #ef9a9a;
}

.theme--dark .client-cabinet-ok {
  color: #81c784;
}

.theme--dark .client-cabinet-muted {
  color: var(--cabinet-muted);
}

.client-cabinet-link {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  font-weight: 500;
}

.client-cabinet-link:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

/* REQ-13: заголовок свёрнутого блока снаружи кнопки — <h2><button> (шаблон аккордеона WAI-ARIA): у <button> содержимое
   только фразовое, заголовок внутри теряет роль. Высоту открытой панели и поворот шеврона Vuetify задаёт селекторами
   прямого потомка (.v-expansion-panel--active > .v-expansion-panel-header) — здесь они же через обёртку. */
.client-cabinet-panel-heading {
  margin: 0;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  border-radius: inherit;
}

.v-expansion-panel--active > .client-cabinet-panel-heading > .v-expansion-panel-header {
  min-height: 64px;
}

.v-expansion-panel--active > .client-cabinet-panel-heading > .v-expansion-panel-header--active .v-expansion-panel-header__icon:not(.v-expansion-panel-header__icon--disable-rotate) .v-icon {
  transform: rotate(-180deg);
}

/* REQ-13: тач-цель флажка — вся строка с подписью, от 44 px. */
.client-cabinet-check .v-input__slot {
  min-height: 44px;
}

.client-cabinet-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.client-cabinet-footer {
  margin-top: 32px;
  text-align: center;
  color: var(--cabinet-muted);
}

@media (min-width: 700px) {
  .client-cabinet {
    padding: 24px 24px 40px;
  }

  .client-cabinet-rental-header {
    padding: 26px 28px;
  }

  .client-cabinet-header__booking {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: end;
  }

  .client-cabinet-action-panel {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    padding: 20px 22px;
  }
}

@media (min-width: 1024px) {
  .client-cabinet {
    padding-top: 30px;
  }

  .client-cabinet-rental-header {
    padding: 30px 34px;
  }

  .client-cabinet-header__brand-row {
    gap: 18px;
  }

  .client-cabinet-header__monogram {
    width: 62px;
    height: 62px;
    font-size: 23px;
  }

  .client-cabinet-header__booking {
    margin-left: 80px;
  }

  .client-cabinet-dashboard {
    grid-template-columns: minmax(0, 1fr) 370px;
    grid-template-areas: "content current";
    align-items: start;
    gap: 28px;
    margin-top: 28px;
  }

  .client-cabinet-sticky {
    position: sticky;
    top: 24px;
  }

  .client-cabinet-plain-section {
    margin-top: 34px;
  }
}

@media (max-width: 520px) {
  .client-cabinet-rental-header {
    padding: 18px;
  }

  .client-cabinet-header__monogram {
    width: 48px;
    height: 48px;
  }

  .client-cabinet-header__booking {
    margin-top: 18px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .client-cabinet *,
  .client-cabinet *::before,
  .client-cabinet *::after {
    scroll-behavior: auto !important;
    transition: none !important;
  }
}
</style>
